<template>
  <AuthLayout
    sidebar-title="Create a new password"
    sidebar-text="Choose a strong password you have not used before."
    :support-phone="supportPhone"
    :is-admin="isAdmin"
    title="Set new password"
    :subtitle="subtitle"
  >
    <form action="javascript:void(0)" @submit.prevent="handleSubmit">
      <p v-if="expiredTokenError" class="auth-login__error-block" role="alert">
        This password request link has expired. Please request a new one by clicking this link:
        <a :href="requestResetHref" @click.prevent="emit('request-new-reset')">Request a new password reset link</a>
      </p>
      <span v-else-if="displayedFormError" class="auth-login__error-block" role="alert">{{ displayedFormError }}</span>

      <AuthPasswordField
        id="reset-password"
        v-model="password"
        label="New password"
        placeholder="Enter new password"
        :error="displayedPasswordError"
        required
        @update:model-value="onPasswordInput"
      />
      <AuthPasswordMeter
        :password="password"
        :email="passwordContext.email"
      />

      <AuthPasswordField
        id="reset-password-confirm"
        v-model="password_confirmation"
        label="Confirm password"
        placeholder="Confirm new password"
        :error="displayedConfirmationError"
        required
        @update:model-value="onConfirmationInput"
      />

      <div class="auth-login__actions">
        <AuthButton type="submit" :loading="loading" :disabled="!canSubmit">
          Reset password
        </AuthButton>
      </div>

      <a :href="requestResetHref" class="auth-login__back auth-login__link--plain">
        ← Back to login
      </a>
    </form>
  </AuthLayout>
</template>

<script setup>
import { computed, ref } from 'vue'
import AuthLayout from '../../shared/components/AuthLayout.vue'
import AuthPasswordField from '../../shared/components/AuthPasswordField.vue'
import AuthButton from '../../shared/components/AuthButton.vue'
import AuthPasswordMeter from '../../shared/components/AuthPasswordMeter.vue'
import {
  evaluatePassword,
  isPasswordValid,
  PASSWORD_CONFIRMATION_MISMATCH_MESSAGE,
} from '../../shared/utils/passwordPolicy.js'

const props = defineProps({
  loading:      { type: Boolean, default: false },
  error:        { type: String,  default: '' },
  passwordError: { type: String, default: '' },
  confirmationError: { type: String, default: '' },
  formError: { type: String, default: '' },
  expiredTokenError: { type: Boolean, default: false },
  supportPhone: { type: String,  default: '815-908-2020' },
  isAdmin:      { type: Boolean, default: false },
  passwordResetRequired: { type: Boolean, default: false },
  passwordContext: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['submit', 'clear-error', 'request-new-reset'])

const password = ref('')
const password_confirmation = ref('')
const localErrors = ref({
  password: '',
  password_confirmation: '',
  form: '',
})

const newPasswordError = computed(() => props.passwordError || props.error || '')
const displayedPasswordError = computed(() => localErrors.value.password || newPasswordError.value)
const displayedConfirmationError = computed(() => localErrors.value.password_confirmation || props.confirmationError)
const displayedFormError = computed(() => localErrors.value.form || props.formError)
const subtitle = computed(() => props.passwordResetRequired
  ? 'Your password has been compromised or does not meet security requirements, please set a new one today.'
  : 'Your new password must be different from previously used passwords.')
const requestResetHref = computed(() => props.isAdmin ? '/admin/login' : '/login')

const canSubmit = computed(() =>
  isPasswordValid(password.value, props.passwordContext)
  && Boolean(password_confirmation.value)
)

const passwordRules = computed(() => evaluatePassword(password.value, props.passwordContext))

const resetLocalErrors = () => {
  localErrors.value = {
    password: '',
    password_confirmation: '',
    form: '',
  }
}

const onPasswordInput = () => {
  localErrors.value = { ...localErrors.value, password: '', form: '' }
  emit('clear-error', 'password')
}

const onConfirmationInput = () => {
  localErrors.value = { ...localErrors.value, password_confirmation: '', form: '' }
  emit('clear-error', 'password_confirmation')
}

const handleSubmit = () => {
  emit('clear-error')
  resetLocalErrors()

  if (!isPasswordValid(password.value, props.passwordContext)) {
    localErrors.value = {
      ...localErrors.value,
      password: firstPasswordRuleError(),
    }
    return
  }

  if (password.value !== password_confirmation.value) {
    localErrors.value = {
      ...localErrors.value,
      password_confirmation: PASSWORD_CONFIRMATION_MISMATCH_MESSAGE,
    }
    return
  }

  emit('submit', {
    password: password.value,
    password_confirmation: password_confirmation.value,
  })
}

function firstPasswordRuleError() {
  const rules = passwordRules.value

  if (!rules.hasMinMaxLength) return 'Password must be 15–64 characters long'
  if (!rules.hasNoSequentialCharacters) return 'Password must not contain more than 4 sequential characters in a row, ascending or descending (e.g., "1234" or "4321")'
  if (!rules.hasNoRepeatedCharacters) return 'Password must not contain the same character repeated more than 4 times in a row (e.g., "aaaa")'
  if (!rules.hasNoPersonalData) return 'Password must not contain your email address'

  return 'Please meet all password requirements.'
}
</script>
